import React from 'react';
import { Calendar, Clock, MapPin, Video, Users, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Meeting } from '../../types/meetings';

interface MeetingListProps {
  meetings: Meeting[];
  onMeetingClick: (meeting: Meeting) => void;
  isLoading?: boolean;
}

export const MeetingList: React.FC<MeetingListProps> = ({ 
  meetings, 
  onMeetingClick, 
  isLoading = false 
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONFIRME':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'ANNULE':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'TERMINE':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      PLANIFIE: 'Planifié',
      CONFIRME: 'Confirmé',
      TERMINE: 'Terminé',
      ANNULE: 'Annulé',
      REPORTE: 'Reporté'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      PLANIFIE: 'bg-orange-50 text-orange-700 border-orange-200',
      CONFIRME: 'bg-green-50 text-green-700 border-green-200',
      TERMINE: 'bg-blue-50 text-blue-700 border-blue-200',
      ANNULE: 'bg-red-50 text-red-700 border-red-200',
      REPORTE: 'bg-yellow-50 text-yellow-700 border-yellow-200'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
                <div className="flex items-center space-x-4">
                  <div className="h-4 bg-gray-200 rounded w-20" />
                  <div className="h-4 bg-gray-200 rounded w-16" />
                </div>
              </div>
              <div className="h-6 bg-gray-200 rounded w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (meetings.length === 0) {
    return (
      <div className="text-center py-12 animate-fade-in">
        <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Aucun rendez-vous</h3>
        <p className="text-gray-500 dark:text-gray-400">Vous n'avez pas de rendez-vous programmés pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {meetings.map((meeting) => (
        <div
          key={meeting.id}
          onClick={() => onMeetingClick(meeting)}
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group animate-slide-up"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">{meeting.title}</h3>
              {meeting.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2 transition-colors duration-200">{meeting.description}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {getStatusIcon(meeting.status)}
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(meeting.status)}`}>
                {getStatusLabel(meeting.status)}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 transition-colors duration-200">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(meeting.start_date), 'dd MMM', { locale: fr })}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>
                  {format(new Date(meeting.start_date), 'HH:mm')} - {format(new Date(meeting.end_date), 'HH:mm')}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                {meeting.meeting_type === 'VIRTUAL' ? (
                  <>
                    <Video className="w-4 h-4" />
                    <span>Visio</span>
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4" />
                    <span>{meeting.location || 'Lieu à définir'}</span>
                  </>
                )}
              </div>
              {meeting.attendees && meeting.attendees.length > 0 && (
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{meeting.attendees.length} participant{meeting.attendees.length > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onMeetingClick(meeting);
              }}
              className="px-3 py-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-all duration-200 text-sm font-medium hover:scale-105 hover:shadow-md"
            >
              {meeting.status === 'CONFIRME' ? 'Rejoindre' : 'Voir détails'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};